/**
 * StepBrandDNA.js
 *
 * Final onboarding step. The structured Brand DNA is generated in the
 * background right after the website analysis (step 1). This step only shows
 * whether it is ready — no field editing here; the user refines it later in
 * Settings → Brand DNA. If it has not appeared after FALLBACK_MS, it is
 * generated here as a safety net.
 */

import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Loader2, Check, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const POLL_INTERVAL = 3000;
const FALLBACK_MS   = 60000;

const StepBrandDNA = ({ onComplete, onBack, inspirations = [] }) => {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const de = lang === 'de';
  const [status, setStatus] = useState(null);   // { ready, company_name, positioning, ... }
  const [fallbackRunning, setFallbackRunning] = useState(false);
  const ready = !!status?.ready;
  const readyRef = useRef(false);
  readyRef.current = ready;

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/onboarding/brand-dna-status`, { headers: getAuthHeader() });
      setStatus(res.data);
      return res.data;
    } catch (_) { return null; }
  };

  useEffect(() => {
    let stopped = false;
    let fallbackTimer = null;

    const poll = async () => {
      const s = await fetchStatus();
      if (stopped || s?.ready) return;
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();

    fallbackTimer = setTimeout(async () => {
      if (stopped || readyRef.current) return;
      const s = await fetchStatus();
      if (s?.ready || stopped) return;
      setFallbackRunning(true);
      try {
        await axios.post(
          `${API}/settings/generate-brand-dna`,
          { inspiration_posts: (inspirations || []).map(i => ({
              platform: i.platform || '', text: i.text || '', what_i_like: i.what_i_like || '' })) },
          { headers: getAuthHeader(), timeout: 120000 }
        );
      } catch (_) { /* background generation keeps going; status polling continues */ }
      setFallbackRunning(false);
    }, FALLBACK_MS);

    return () => { stopped = true; clearTimeout(fallbackTimer); };
  }, []); // eslint-disable-line

  return (
    <div data-testid="step-brand-dna" className="space-y-6">
      <div className="text-center py-10">
        {ready ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#00D9C8] mx-auto mb-4" />
            <h2 className="text-2xl font-bold tracking-tight text-white mb-2" data-testid="brand-dna-ready">
              {de ? 'Alles bereit!' : 'All set!'}
            </h2>
            <p className="text-[#A0AEC0] text-sm max-w-md mx-auto">
              {de
                ? `${status.company_name ? status.company_name + ': ' : ''}Deine Brand DNA ist fertig. Du kannst sie jederzeit in den Einstellungen ansehen und anpassen.`
                : `${status.company_name ? status.company_name + ': ' : ''}Your Brand DNA is ready. You can review and adjust it anytime in Settings.`}
            </p>
            {status.positioning && (
              <p className="text-white/80 text-sm italic max-w-md mx-auto mt-4">„{status.positioning}“</p>
            )}
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-[#00D9C8] animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold tracking-tight text-white mb-2">
              {de ? 'Wir erstellen deine Brand DNA…' : 'Building your Brand DNA…'}
            </h2>
            <p className="text-[#A0AEC0] text-sm max-w-md mx-auto">
              {fallbackRunning
                ? (de ? 'Das dauert noch einen Moment.' : 'This will take a moment.')
                : (de
                    ? 'Das läuft im Hintergrund. Du kannst auch schon weitergehen — sie erscheint dann in den Einstellungen.'
                    : 'This runs in the background. You can continue now — it will show up in Settings.')}
            </p>
          </>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-[#1A2B42] text-[#A0AEC0]"
          data-testid="brand-dna-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {de ? 'Zurück' : 'Back'}
        </Button>
        <Button
          onClick={() => onComplete()}
          className="flex-1 h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold"
          data-testid="brand-dna-continue-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          {ready ? (de ? 'Weiter' : 'Continue') : (de ? 'Im Hintergrund fertigstellen & weiter' : 'Finish in background & continue')}
        </Button>
      </div>
    </div>
  );
};

export default StepBrandDNA;
